import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';
import { useTheme } from '../../ThemeContext';
import { ModalShell } from '../ui';


export default function CommentModal({ show, onClose, currentPost, currentUser, onCommentAdded, setShowConnectModal }) {
  const { t, isDark } = useTheme();
  const [comment, setComment] = useState('');
  const [comments, setComments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingComments, setLoadingComments] = useState(true);

  // Fetch comments with author info
  useEffect(() => {
    if (show && currentPost?.id) {
      fetchComments();
    }
  }, [show, currentPost?.id]);

  const fetchComments = async () => {
    setLoadingComments(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .select(`
          *,
          author:authors(name, avatar_url)
        `)
        .eq('post_id', currentPost.id)
        .order('created_at', { ascending: false });

      if (error) throw error;
      setComments(data || []);
    } catch (error) {
      console.error('Error fetching comments:', error);
    } finally {
      setLoadingComments(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Check if user is logged in as author
    if (!currentUser?.id) {
      alert('You must be signed in as an author to comment. Please connect your wallet first.');
      return;
    }

    if (!comment.trim()) return;

    setLoading(true);
    try {
      const { data, error } = await supabase
        .from('comments')
        .insert({
          post_id: currentPost.id,
          author_id: currentUser.id,
          content: comment.trim()
        })
        .select(`
          *,
          author:authors(name, avatar_url)
        `)
        .single();

      if (error) throw error;

      // Add new comment to list
      setComments([data, ...comments]);
      setComment('');
      onCommentAdded?.();
    } catch (error) {
      console.error('Error adding comment:', error);
      alert('Failed to add comment. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  if (!show) return null;

  return (
    <ModalShell onClose={onClose} maxWidth="600px" panelStyle={{ backgroundColor: t.modalBg, padding: '2rem', borderRadius: '8px', boxShadow: '0 20px 25px -5px rgba(0,0,0,0.1)' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ 
            fontFamily: 'Crimson Pro, serif', 
            fontSize: '2rem',
            color: t.text,
            margin: 0
          }}>
            Comments ({comments.length})
          </h2>
          <button
            onClick={onClose}
            style={{
              backgroundColor: 'transparent',
              border: 'none',
              fontSize: '1.5rem',
              cursor: 'pointer',
              color: t.text
            }}
          >
            ×
          </button>
        </div>

        {/* Comment Form */}
        {currentUser?.id ? (
          <form onSubmit={handleSubmit} style={{ marginBottom: '2rem' }}>
            <textarea
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              placeholder="Share your thoughts..."
              rows="4"
              style={{
                width: '100%',
                padding: '1rem',
                border: `2px solid ${t.accent}`,
                borderRadius: '4px',
                backgroundColor: t.surface,
                color: t.text,
                fontFamily: 'Crimson Pro, serif',
                fontSize: '1rem',
                resize: 'vertical',
                marginBottom: '1rem'
              }}
            />
            <button
              type="submit"
              disabled={loading || !comment.trim()}
              style={{
                padding: '0.75rem 1.5rem',
                backgroundColor: t.buttonBg,
                color: t.buttonText,
                border: 'none',
                borderRadius: '4px',
                cursor: (loading || !comment.trim()) ? 'not-allowed' : 'pointer',
                fontFamily: 'Space Mono, monospace',
                opacity: (loading || !comment.trim()) ? 0.6 : 1
              }}
            >
              {loading ? 'Posting...' : 'Post Comment'}
            </button>
          </form>
        ) : (
          <div style={{
            padding: '1.5rem',
            backgroundColor: 'rgba(61, 36, 18, 0.1)',
            borderRadius: '4px',
            marginBottom: '2rem',
            textAlign: 'center'
          }}>
            <p style={{ 
              color: t.text,
              fontFamily: 'Space Mono, monospace',
              margin: 0,
              marginBottom: '0.5rem'
            }}>
              Connect your wallet to leave a comment
            </p>
            <p style={{ 
              color: t.text,
              fontFamily: 'Crimson Pro, serif',
              fontSize: '0.875rem',
              opacity: 0.7,
              margin: 0
            }}>
              You'll need to connect your wallet and create an author profile
            </p>
            <button
              onClick={() => {
                onClose();
                setShowConnectModal(true);
              }}
              style={{
                marginTop: '1rem',
                padding: '0.5rem 1rem',
                backgroundColor: t.buttonBg,
                color: t.buttonText,
                border: 'none',
                borderRadius: '4px',
                cursor: 'pointer',
                fontFamily: 'Space Mono, monospace'
              }}
            >
              Connect Wallet
            </button>
          </div>
        )}

        {/* Comments List */}
        {loadingComments ? (
          <p style={{ textAlign: 'center', color: t.text, opacity: 0.6 }}>
            Loading comments...
          </p>
        ) : comments.length === 0 ? (
          <p style={{ textAlign: 'center', color: t.text, opacity: 0.6 }}>
            No comments yet. Be the first!
          </p>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: '1.5rem' }}>
            {comments.map((c) => (
              <div 
                key={c.id}
                style={{
                  padding: '1rem',
                  backgroundColor: t.surface,
                  borderRadius: '4px',
                  border: '1px solid rgba(61, 36, 18, 0.2)'
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', marginBottom: '0.75rem' }}>
                  {c.author?.avatar_url ? (
                    <img 
                      src={c.author.avatar_url} 
                      alt={c.author.name}
                      style={{
                        width: '32px',
                        height: '32px',
                        borderRadius: '50%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <div style={{
                      width: '32px',
                      height: '32px',
                      borderRadius: '50%',
                      backgroundColor: t.buttonBg,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      color: t.buttonText,
                      fontFamily: 'Space Mono, monospace',
                      fontSize: '0.875rem',
                      fontWeight: 'bold'
                    }}>
                      {c.author?.name?.charAt(0).toUpperCase()}
                    </div>
                  )}
                  <div>
                    <p style={{ 
                      fontFamily: 'Space Mono, monospace',
                      fontSize: '0.875rem',
                      fontWeight: 'bold',
                      color: t.text,
                      margin: 0
                    }}>
                      {c.author?.name || 'Unknown Author'}
                    </p>
                    <p style={{ 
                      fontFamily: 'Space Mono, monospace',
                      fontSize: '0.75rem',
                      color: t.text,
                      opacity: 0.6,
                      margin: 0
                    }}>
                      {new Date(c.created_at).toLocaleDateString('en-US', {
                        month: 'short',
                        day: 'numeric',
                        year: 'numeric'
                      })}
                    </p>
                  </div>
                </div>
                <p style={{ 
                  fontFamily: 'Crimson Pro, serif',
                  fontSize: '1rem',
                  color: t.text,
                  margin: 0,
                  whiteSpace: 'pre-wrap'
                }}>
                  {c.content}
                </p>
              </div>
            ))}
          </div>
        )}
    </ModalShell>
  );
}
